import { useEffect, useState } from "react";
import { DashboardLayout } from "@/projects/components/dashboard-layout";
import { Card } from "@/projects/components/ui/card";
import { fetchMyProfile, type GyanProfile } from "@/projects/lib/authApi";
import { Loader2 } from "lucide-react";
import { toast } from "@/projects/lib/toast";
import { cn } from "@/projects/lib/utils";

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b py-2.5 last:border-b-0">
      <span className="text-muted-foreground shrink-0">{k}</span>
      <span className="text-right font-medium">{v}</span>
    </div>
  );
}

function statusLabel(status?: string) {
  if (!status) {
    return "—";
  }
  const v = status.trim().toLowerCase();
  if (v === "active") {
    return "Active";
  }
  if (["inactive", "disabled", "deactivated"].includes(v)) {
    return "Inactive";
  }
  return status;
}

function isActiveStatus(status?: string) {
  return (status ?? "").trim().toLowerCase() === "active";
}

export function TeacherProfilePage() {
  const [profile, setProfile] = useState<GyanProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const data = await fetchMyProfile();
        if (!cancelled) {
          setProfile(data);
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(
            error instanceof Error
              ? error.message
              : "Unable to load your profile."
          );
          setProfile(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const displayName =
    profile?.teacher_name?.trim() ||
    profile?.full_name?.trim() ||
    profile?.username ||
    "Teacher";
  const initial = displayName?.[0]?.toUpperCase() ?? "T";
  const subject = profile?.subject_department?.trim() || "—";
  const active = isActiveStatus(profile?.status);

  return (
    <DashboardLayout
      role="teacher"
      title="Profile"
      breadcrumb={["Home", "Profile"]}
    >
      <Card className="max-w-xl p-6">
        {loading ? (
          <div className="text-muted-foreground flex items-center justify-center gap-2 py-16 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading profile…
          </div>
        ) : !profile ? (
          <div className="text-muted-foreground py-16 text-center text-sm">
            Unable to load profile.
          </div>
        ) : (
          <>
            <div className="flex items-center gap-4">
              <div className="grid h-16 w-16 place-items-center rounded-full border-2 border-[#f0c14d]/50 bg-[#0f2a6b] text-xl font-bold text-white shadow-sm">
                {initial}
              </div>
              <div className="min-w-0">
                <div className="truncate text-lg font-semibold">
                  {displayName}
                </div>
                <div className="text-muted-foreground text-sm">{subject}</div>
              </div>
            </div>

            <div className="mt-6 grid gap-1 text-sm">
              <Row
                k="Teacher ID"
                v={String(profile.teacher_id ?? profile.id ?? "—")}
              />
              <Row k="Username" v={profile.username ?? "—"} />
              <Row k="Full Name" v={profile.full_name ?? displayName} />
              <Row k="Email" v={profile.email ?? "—"} />
              <Row k="Mobile" v={profile.mobile ?? "—"} />
              <Row k="Subject / Department" v={subject} />
              <Row k="Role" v={profile.role ?? "teacher"} />
              <Row
                k="Status"
                v={
                  <span
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
                      active
                        ? "border-success/30 bg-success/10 text-success"
                        : "border-muted-foreground/30 bg-muted text-muted-foreground"
                    )}
                  >
                    <span
                      className={cn(
                        "h-1.5 w-1.5 rounded-full",
                        active ? "bg-success" : "bg-muted-foreground"
                      )}
                    />
                    {statusLabel(profile.status)}
                  </span>
                }
              />
            </div>
          </>
        )}
      </Card>
    </DashboardLayout>
  );
}
